import {useEffect, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import useAlerts from './use-alerts';
import useServices from './use-services';
import useStore from './use-store';

/**
 * Salva a edicao do odometro e do horimetro de um dispositivo e atualiza o card.
 *
 * BUSINESS RULE: o servidor recusa a edicao fora das regras e a recusa chega traduzida; quando a edicao liga o
 * odometro da plataforma, os relatorios do veiculo mudam de calculo e o usuario recebe o aviso.
 *
 * CONSTRAINT: um pedido de salvar com outro em andamento e descartado antes de chegar ao servidor; o segundo clique ou
 * F8 nao repete a edicao nem a mensagem.
 *
 * @return {{save: function(Object): Promise<boolean>, saving: boolean}} `save` recebe
 *         `{deviceId, totalDistance, hours}` e resolve verdadeiro quando o servidor aceitou; `saving` e verdadeiro
 *         enquanto um pedido esta em andamento, para desabilitar o formulario
 */
const useSaveAccumulator = () => {
    const intl = useIntl();
    const {toast} = useAlerts();
    const {deviceService} = useServices();
    const {store} = useStore();
    const inFlight = useRef(false);
    const [saving, setSaving] = useState(false);
    const mounted = useRef(true);

    useEffect(() => () => {
        mounted.current = false;
    }, []);

    const save = async ({deviceId, totalDistance, hours}) => {
        if (inFlight.current) {
            return false;
        }
        inFlight.current = true;
        setSaving(true);
        try {
            const result = await deviceService.accumulator(deviceId, totalDistance, hours);
            store.devices.updateDeviceAccumulator(deviceId, {
                totalDistance, hours, ignoreOdometerEnabled: !!result?.ignoreOdometerEnabled});
            toast.success(intl.formatMessage({id: 'sharedUpdatedSuccess'}));
            if (result?.ignoreOdometerEnabled) {
                toast.warn(intl.formatMessage({id: 'accumulatorPlatformOdometerEnabled'}));
            }
            return true;
        } catch (e) {
            toast.errorStatus(e, {legacy: false});
            return false;
        } finally {
            inFlight.current = false;
            if (mounted.current) {
                setSaving(false);
            }
        }
    };

    return {save, saving};
};

export default useSaveAccumulator;
